import React, { useCallback, useEffect, useRef, useState } from "react";
import { startSession, endSession } from "../../services/GameService";
import GameLayoutTopbar from "../layouts/GameLayoutTopbar";
import GameOverScreen from "../GameOverScreen";
import { usePlayClock } from "../common/usePlayClock";
import fireworksData from "../FindMiddle/animations/fireworks.json";
import FACES from "./faces";
import {
  parseLevelParams,
  dealClubs,
  generateMembers,
  buildQuiz,
  genreById,
  scoreGame,
} from "./bookClub";
import "./BookClubGameForm.css";

// Pauses after an answer (longer after a mistake so the right answer can be
// seen) and between rounds.
const CORRECT_PAUSE_MS = 900;
const MISTAKE_PAUSE_MS = 2000;
const ROUND_BREAK_MS = 2200;

const Portrait = ({ face, className = "", alt = "" }) => (
  <img
    className={`bc-avatar ${className}`.trim()}
    src={face.src}
    alt={alt}
    data-face={face.id}
    draggable="false"
  />
);

// A favourite kind of book, drawn as a small book cover: a coloured cover
// with a spine, a big icon and the genre on a label.
const BookCover = ({ genreId, size = "medium" }) => {
  const genre = genreById(genreId);
  return (
    <span className={`bc-book bc-book--${size}`} style={{ "--book-color": genre.color }}>
      <span className="bc-book__icon" aria-hidden="true">
        {genre.icon}
      </span>
      <span className="bc-book__title">{genre.label}</span>
    </span>
  );
};

// Book Club — a face–name association memory game. Each round the player meets
// a few members of the book club (a photo, a name and, at higher levels, the
// kind of book they love), then the cards are put away and the player answers
// one question per member by tapping a choice. The player's level sets the
// club size, study time, number of choices, which questions are asked and how
// alike the clubs are (core/data/game_levels.json). Faces come from the photo
// pool in faces/; `faces` and `rng` are injectable so tests are deterministic.
const BookClubGameForm = ({
  gameRoutePath = "book-club",
  faces = FACES,
  rng = Math.random,
}) => {
  const [phase, setPhase] = useState("loading"); // loading|ready|study|quiz|break|over
  const [levelNumber, setLevelNumber] = useState(1);
  const [params, setParams] = useState(parseLevelParams(null));
  const [round, setRound] = useState(0); // 0-based index of the current round
  const [members, setMembers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [picked, setPicked] = useState(null); // the option chosen for this question
  const [roundCorrect, setRoundCorrect] = useState(0);

  const sessionIdRef = useRef(null);
  // The in-flight endSession (it also promotes/demotes the level); a restart
  // waits for it so the next game loads the new level.
  const savingRef = useRef(null);
  const playClock = usePlayClock();
  const timersRef = useRef([]);
  const clubsRef = useRef([]); // the faces dealt for each round of this game
  const correctRef = useRef(0);
  const askedRef = useRef(0);

  const schedule = useCallback((fn, ms) => {
    timersRef.current.push(setTimeout(fn, ms));
  }, []);
  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  // Load the player's level. Anonymous players get Level 1 with no session
  // (startSession handles that).
  const setupGame = useCallback(async () => {
    clearTimers();
    setPhase("loading");
    let level = null;
    try {
      if (savingRef.current) await savingRef.current;
      savingRef.current = null;
      const session = await startSession(gameRoutePath);
      sessionIdRef.current = (session && session.sessionId) || null;
      level = session && session.level;
    } catch (e) {
      // play with defaults
    }
    setLevelNumber((level && level.level_number) || 1);
    setParams(parseLevelParams(level));
    setRound(0);
    correctRef.current = 0;
    askedRef.current = 0;
    setPhase("ready");
  }, [gameRoutePath]);

  useEffect(() => {
    setupGame();
    return clearTimers;
  }, [setupGame]);

  const startQuiz = () => {
    clearTimers();
    setQuestionIndex(0);
    setPicked(null);
    setPhase("quiz");
  };

  // Deal every round's faces up front (so none repeat) and start loading the
  // photos while the player studies the first club.
  const startGame = () => {
    clubsRef.current = dealClubs(faces, params.rounds, params.members, params.similarity, rng);
    clubsRef.current.flat().forEach((face) => {
      const img = new Image();
      img.src = face.src;
    });
    playClock.start();
    startRound(0);
  };

  // A new round: a fresh club to meet, studied for a time that grows with the
  // club's size. The player can move on early with "I'm ready".
  const startRound = (roundIndex) => {
    clearTimers();
    const club = generateMembers(clubsRef.current[roundIndex], params.similarity, rng);
    setMembers(club);
    setQuestions(buildQuiz(club, params.questionTypes, params.options, rng));
    setRound(roundIndex);
    setRoundCorrect(0);
    setPhase("study");
    schedule(startQuiz, club.length * params.studyMsPerMember);
  };

  const finishGame = () => {
    setPhase("over");
    if (sessionIdRef.current) {
      savingRef.current = endSession(
        sessionIdRef.current,
        scoreGame(correctRef.current, askedRef.current),
        playClock.elapsedMs(),
      );
    }
  };

  const handleAnswer = (option) => {
    if (phase !== "quiz" || picked !== null) return;
    const question = questions[questionIndex];
    const correct = option === question.answer;
    setPicked(option);
    askedRef.current += 1;
    if (correct) {
      correctRef.current += 1;
      setRoundCorrect((n) => n + 1);
    }
    // Last answer in: play time stops here, not after the closing pause.
    if (questionIndex + 1 >= questions.length && round + 1 >= params.rounds) {
      playClock.stop();
    }
    schedule(() => {
      if (questionIndex + 1 < questions.length) {
        setQuestionIndex(questionIndex + 1);
        setPicked(null);
      } else if (round + 1 < params.rounds) {
        setPhase("break");
        schedule(() => startRound(round + 1), ROUND_BREAK_MS);
      } else {
        finishGame();
      }
    }, correct ? CORRECT_PAUSE_MS : MISTAKE_PAUSE_MS);
  };

  const asksGenres =
    params.questionTypes.includes("genre") || params.questionTypes.includes("reader");

  const instruction = () => {
    switch (phase) {
      case "ready":
        return "Meet the members of the book club, then show who's who.";
      case "study":
        return asksGenres
          ? "Remember each member's name and what they love to read."
          : "Remember each member's name.";
      case "quiz":
        return `Who's who? Tap your answer (question ${questionIndex + 1} of ${questions.length}).`;
      case "break":
        return "New members are joining the club…";
      default:
        return "Setting out the chairs…";
    }
  };

  const memberById = (id) => members.find((m) => m.id === id);

  const optionClass = (option, answer) => {
    if (picked === null) return "bc-option";
    if (option === answer) return "bc-option bc-option--correct";
    if (option === picked) return "bc-option bc-option--wrong";
    return "bc-option bc-option--faded";
  };

  const renderQuestion = () => {
    const q = questions[questionIndex];
    const member = memberById(q.memberId);
    let prompt;
    let cue = null;
    if (q.type === "name") {
      prompt = "What is this person's name?";
      cue = <Portrait face={member.face} className="bc-cue-face" alt="The member to name" />;
    } else if (q.type === "genre") {
      prompt = "What does this person love to read?";
      cue = <Portrait face={member.face} className="bc-cue-face" alt="The member to remember" />;
    } else if (q.type === "face") {
      prompt = `Which one is ${member.name}?`;
    } else {
      prompt = "Who loves this kind of book?";
      cue = <BookCover genreId={member.genre} size="large" />;
    }

    const facesAsOptions = q.type === "face" || q.type === "reader";
    return (
      <div className="bc-question" key={questionIndex}>
        <h3 className="bc-prompt">{prompt}</h3>
        {cue && <div className="bc-cue">{cue}</div>}
        <div className={`bc-options${facesAsOptions ? " bc-options--faces" : ""}`}>
          {q.options.map((option, i) => (
            <button
              key={option}
              type="button"
              className={optionClass(option, q.answer)}
              onClick={() => handleAnswer(option)}
              disabled={picked !== null}
              aria-label={facesAsOptions ? `Member ${i + 1}` : undefined}
            >
              {facesAsOptions && <Portrait face={memberById(option).face} />}
              {q.type === "name" && option}
              {q.type === "genre" && <BookCover genreId={option} size="small" />}
              {picked !== null && option === q.answer && (
                <span className="bc-mark bc-mark--correct" aria-label="right answer">✓</span>
              )}
              {picked !== null && option === picked && option !== q.answer && (
                <span className="bc-mark bc-mark--wrong" aria-label="your answer">✗</span>
              )}
            </button>
          ))}
        </div>
        {picked !== null && (
          <p className="bc-feedback" role="status">
            {picked === q.answer ? "Well remembered! 📚" : "Not quite — the right answer is highlighted."}
          </p>
        )}
      </div>
    );
  };

  if (phase === "over") {
    const asked = askedRef.current;
    const correct = correctRef.current;
    return (
      <GameLayoutTopbar
        infoPanel={<h2 className="heading-secondary">Book Club — Game Over</h2>}
        gameArea={
          <GameOverScreen
            stats={[
              `You remembered ${correct} of ${asked} answers.`,
              `Score: ${scoreGame(correct, asked)}%`,
            ]}
            onRestart={setupGame}
            showFireworks={correct > 0}
            fireworksAnimation={fireworksData}
          />
        }
      />
    );
  }

  return (
    <GameLayoutTopbar
      className="book-club"
      infoPanel={
        <>
          <h2 className="heading-secondary">Level {levelNumber}</h2>
          <h3 className="heading-tertiary">
            Book Club
            {phase !== "loading" &&
              phase !== "ready" &&
              ` — Round ${round + 1} of ${params.rounds}`}
          </h3>
          <p className="text-muted bc-instruction" role="status">
            {instruction()}
          </p>
        </>
      }
      gameArea={
        <div className="bc-room">
          {phase === "ready" && (
            <div className="bc-intro">
              <p>
                {params.members} members are waiting to meet you. Study their faces
                {asksGenres ? ", names and favourite books" : " and names"}, then
                answer a question about each of them.
              </p>
              <button
                type="button"
                className="btn btn-primary bc-start"
                onClick={startGame}
              >
                Start
              </button>
            </div>
          )}

          {phase === "study" && (
            <div className="bc-study">
              <div className="bc-timer" aria-hidden="true">
                <div
                  key={round}
                  className="bc-timer__bar"
                  style={{ animationDuration: `${members.length * params.studyMsPerMember}ms` }}
                />
              </div>
              <ul className="bc-members">
                {members.map((m) => (
                  <li key={m.id} className="bc-card">
                    <Portrait face={m.face} alt={m.name} />
                    <span className="bc-card__name">{m.name}</span>
                    {asksGenres && <BookCover genreId={m.genre} />}
                  </li>
                ))}
              </ul>
              <button type="button" className="btn btn-primary bc-ready" onClick={startQuiz}>
                I'm ready
              </button>
            </div>
          )}

          {phase === "quiz" && questions.length > 0 && renderQuestion()}

          {phase === "break" && (
            <div className="bc-break">
              <p className="bc-break__score">
                Round {round + 1}: {roundCorrect} of {questions.length} right
              </p>
            </div>
          )}
        </div>
      }
    />
  );
};

export default BookClubGameForm;
